/* ------------------------------------------------------------------ */
/*  Cognito / auth configuration shared by middleware and hooks        */
/* ------------------------------------------------------------------ */

import { getEnv } from "../env";

/** Cognito pool configuration sourced from environment variables. */
export const cognitoConfig = {
  region: getEnv("REACT_APP_COGNITO_REGION") || getEnv("COGNITO_REGION"),
  userPoolId:
    getEnv("REACT_APP_COGNITO_USER_POOL_ID") || getEnv("COGNITO_USER_POOL_ID"),
  clientId:
    getEnv("REACT_APP_COGNITO_WEB_CLIENT_ID") ||
    getEnv("COGNITO_WEB_CLIENT_ID"),
} as const;

/* ---- Cognito Hosted UI URLs -------------------------------------- */

const COGNITO_AUTH_URL =
  getEnv("REACT_APP_COGNITO_AUTH_URL") || getEnv("COGNITO_AUTH_URL");
const COGNITO_TOKEN_URL =
  getEnv("REACT_APP_COGNITO_TOKEN_URL") || getEnv("COGNITO_TOKEN_URL");
const COGNITO_LOGOUT_URL =
  getEnv("REACT_APP_COGNITO_LOGOUT_URL") || getEnv("COGNITO_LOGOUT_URL");
export { COGNITO_TOKEN_URL };

/**
 * Derive the marketplace origin from the current context:
 *  1. Browser: window.location.origin
 *  2. Server with proxy headers: x-forwarded-host (CloudFront → Lambda)
 *  3. Env var fallback (build-time or Lambda runtime)
 *
 * We intentionally avoid deriving origin from request.url because behind
 * CloudFront the Lambda receives its own Function URL, not the public domain.
 */
export function resolveOrigin(request?: Request): string {
  if (typeof window !== "undefined") return window.location.origin;

  if (request) {
    const fwdHost = request.headers.get("x-forwarded-host");
    if (fwdHost) {
      const proto = request.headers.get("x-forwarded-proto") || "https";
      return `${proto}://${fwdHost.split(",")[0].trim()}`;
    }
  }

  return (
    getEnv("REACT_APP_MARKETPLACE_BASE_URL") ||
    getEnv("MARKETPLACE_BASE_URL") ||
    getEnv("REACT_APP_SITE_URL")
  );
}

/* ---- Cookie names ------------------------------------------------ */

// `__Host-` prefix: browser-enforced host-locking (requires Secure, Path=/, no Domain).
// `.at` = access token, `.it` = id token.
export const ACCESS_TOKEN_COOKIE = "__Host-rayt.at";
export const ID_TOKEN_COOKIE = "__Host-rayt.it";
export const REFRESH_TOKEN_COOKIE = "refresh_token";

/* ---- Public paths ------------------------------------------------ */

/** Static paths that never require authentication. */
const PUBLIC_PATHS: readonly string[] = [
  "/",
  "/merchants",
  "/services",
  "/about",
  "/cart",
];

/**
 * Returns `true` when `pathname` can be accessed without a session.
 *
 * Handles:
 * - exact matches for static public paths
 * - `/merchants/<id>` (but NOT deeper sub-paths like `/merchants/<id>/locations`)
 * - `/services/*` — all service browsing pages
 * - `/api/auth/*` callback and logout routes

 */
export function isPublicPath(pathname: string): boolean {
  if (PUBLIC_PATHS.includes(pathname)) return true;

  // Auth callback/logout routes must be publicly accessible
  if (pathname.startsWith("/api/auth/")) return true;

  // All /services/* pages are public (category browsing, merchant items)
  if (pathname.startsWith("/services/")) return true;

  // Guest shopping routes — no login required
  if (pathname.startsWith("/buy/")) return true;

  // Match /merchants/<id> — exactly one segment after /merchants/
  const merchantDetail = /^\/merchants\/[^/]+$/;
  return merchantDetail.test(pathname);
}

/* ---- Redirect helpers -------------------------------------------- */

/** OAuth callback path on the marketplace. */
const AUTH_CALLBACK_PATH = "/api/auth/callback";

/**
 * Build the Cognito Hosted UI authorize URL.
 *
 * Uses the OAuth2 Authorization Code flow. `state` is the opaque value Cognito
 * echoes back to the callback — it carries the CSRF nonce plus the return-to
 * path (see {@link createAuthState}). Mint it via `/api/auth/login`; never pass
 * a raw return path here, or the callback's nonce check will reject it.
 */
export function getAuthorizeUrl(
  state: string,
  requestOrOrigin?: Request | string,
): string {
  const origin =
    typeof requestOrOrigin === "string"
      ? requestOrOrigin
      : resolveOrigin(requestOrOrigin);
  const redirectUri = `${origin}${AUTH_CALLBACK_PATH}`;
  const params = new URLSearchParams({
    response_type: "code",
    client_id: cognitoConfig.clientId,
    redirect_uri: redirectUri,
    scope: "openid email profile",
    state,
  });
  return `${COGNITO_AUTH_URL}?${params.toString()}`;
}

/**
 * Build the Cognito Hosted UI signup URL.
 *
 * Cognito's `/signup` endpoint accepts the same OAuth2 parameters as
 * `/oauth2/authorize` but lands on the registration form instead of login.
 * `state` must be a nonce-bound value minted by {@link createAuthState} —
 * same as {@link getAuthorizeUrl} — since the callback validates it either way.
 */
export function getSignUpAuthorizeUrl(
  state: string,
  requestOrOrigin?: Request | string,
): string {
  const origin =
    typeof requestOrOrigin === "string"
      ? requestOrOrigin
      : resolveOrigin(requestOrOrigin);
  const redirectUri = `${origin}${AUTH_CALLBACK_PATH}`;
  const params = new URLSearchParams({
    response_type: "code",
    client_id: cognitoConfig.clientId,
    redirect_uri: redirectUri,
    scope: "openid email profile",
    state,
  });
  const authBase = COGNITO_AUTH_URL.replace(/\/oauth2\/authorize$/, "");
  return `${authBase}/signup?${params.toString()}`;
}

/**
 * Build the marketplace login URL that initiates an OAuth flow.
 *
 * Sign-in CTAs point here instead of straight at Cognito so the server can
 * mint a one-time CSRF nonce (set as the `auth_state` cookie) before handing
 * off to the Hosted UI. `returnTo` is the path to land on after sign-in.
 */
export function getLoginUrl(returnTo: string): string {
  return `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * Build the marketplace signup URL that initiates an OAuth flow.
 *
 * Same nonce-minting `/api/auth/login` route as {@link getLoginUrl}, flagged
 * with `mode=signUp` so it lands on Cognito's registration form instead of
 * the login form.
 */
export function getSignUpUrl(returnTo: string): string {
  return `/api/auth/login?mode=signUp&returnTo=${encodeURIComponent(returnTo)}`;
}

/** Build the Cognito Hosted UI logout URL. */
export function getLogoutUrl(request?: Request): string {
  const origin = resolveOrigin(request);
  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    logout_uri: origin,
  });
  return `${COGNITO_LOGOUT_URL}?${params.toString()}`;
}

/** The redirect_uri that must match the Cognito app client config. */
export function getRedirectUri(request?: Request): string {
  const origin = resolveOrigin(request);
  return `${origin}${AUTH_CALLBACK_PATH}`;
}
